import { FormEvent, useMemo, useState, useEffect } from 'react';
import classNames from 'classnames';

import {
  useDeleteAlertConfigurationMutation,
  useSlackChannelsQuery,
  useUpsertAlertConfigurationMutation,
} from '@/graphql';
import { getMessage } from '@/lib/error';
import { useSelectedAccount } from '@/lib/accounts/context';
import { getFinalState, PipelineStateContext } from '@/explore/pipeline/state';
import { createFieldOption } from '@/explore/components/select-options/field';
import { getModelOrThrow } from '@/explore/model/utils';
import { Icon } from '@/components/icon';
import { Modal } from '@/components/modal';
import { OverlayContextProvider } from '@/components/overlay/context';
import { Panel } from '@/components/panel';
import { useToastContext } from '@/components/toast';
import { TextArea } from '@/components/form/text-area';
import { TagSelect } from '@/components/form/tag-select';
import { Button, IconButton, InlineButton } from '@/components/button';
import { RadioButton } from '@/components/form/radio-button';
import { Input } from '@/components/form/input';
import { SearchInput } from '@/components/form/search-input';
import { createDereferencedPipeline, extractSubpipelines } from '@/core/pipeline';
import { buildExplorationFromPipelines } from '@/core/exploration';
import { flattenPipeline } from '@/explore/pipeline/utils';
import { Loader } from '@/components/loader';

import { AlertConfiguration, NotificationConfig } from '../types';
import { IntegrationListItem } from '../integrations/list';

import formStyles from '../../components/form/form.module.scss';
import styles from './edit-alert.module.scss';

const getWebhookUrls = (notificationConfig: NotificationConfig): string[] => {
  switch (notificationConfig.type) {
    case 'webhook':
      return notificationConfig.urls;
    case 'slack':
      return notificationConfig.webhook_urls;
    case 'slack_channel':
      return [];
  }
};

const isValidNotificationConfig = (notificationConfig: NotificationConfig): boolean => {
  switch (notificationConfig.type) {
    case 'webhook':
      return (
        notificationConfig.urls.length > 0 && notificationConfig.urls.every((url) => url.length > 0)
      );
    case 'slack':
      return (
        notificationConfig.webhook_urls.length > 0 &&
        notificationConfig.webhook_urls.every((url) => url.length > 0)
      );
    case 'slack_channel':
      return notificationConfig.channelId.length > 0;
  }
};

const isValid = (alertConfiguration: AlertConfiguration) => {
  return (
    alertConfiguration.name.length > 0 &&
    alertConfiguration.keyFields.length > 0 &&
    isValidNotificationConfig(alertConfiguration.notificationConfig)
  );
};

interface EditAlertFormProps {
  alertConfiguration: AlertConfiguration;
  ctx: Omit<PipelineStateContext, 'exploration'>;
  integrations: IntegrationListItem[];
  onSubmit: (alertConfiguration: AlertConfiguration) => void;
  onCancel: () => void;
  onDelete?: () => void;
  upsertLoading: boolean;
  deleteLoading: boolean;
}

const EditAlertForm = (props: EditAlertFormProps) => {
  const {
    alertConfiguration,
    ctx,
    integrations,
    onSubmit,
    onCancel,
    onDelete,
    upsertLoading,
    deleteLoading,
  } = props;
  const account = useSelectedAccount();
  const [name, setName] = useState<string>(alertConfiguration.name);
  const [message, setMessage] = useState<string>(alertConfiguration.message ?? '');
  const [keyFields, setKeyFields] = useState<string[]>(alertConfiguration.keyFields);
  const [displayColumnsFilter, setDisplayColumnsFilter] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const slackIntegration = integrations.find((i) => i.service.toLowerCase() === 'slack');
  const hasSlackIntegration = slackIntegration !== undefined;

  const isNewAlert = alertConfiguration.alertConfigurationId === undefined;

  const [notificationType, setNotificationType] = useState<NotificationConfig['type']>(
    alertConfiguration.notificationConfig.type,
  );

  const getInitialChannelId = () => {
    if (alertConfiguration.notificationConfig.type === 'slack_channel') {
      return alertConfiguration.notificationConfig.channelId;
    }
    return '';
  };

  const [selectedChannelId, setSelectedChannelId] = useState<string>(getInitialChannelId());
  const [webhookUrls, setWebhookUrls] = useState<string[]>(
    getWebhookUrls(alertConfiguration.notificationConfig),
  );

  const { data: channelsData, loading: isChannelsLoading } = useSlackChannelsQuery({
    variables: {
      accountId: account.accountId,
      integrationId: slackIntegration?.integrationId ?? '',
    },
    skip: slackIntegration === undefined || notificationType !== 'slack_channel',
  });

  const slackChannels = channelsData?.account?.slackChannels ?? [];

  const { pipeline, subPipelines } = extractSubpipelines(alertConfiguration.pipeline);
  const exploration = buildExplorationFromPipelines(
    alertConfiguration.name,
    [pipeline, ...subPipelines],
    ctx.models,
  );
  const flattenedPipeline = flattenPipeline(pipeline, exploration);
  const model = getModelOrThrow(ctx.models, flattenedPipeline.baseModelId);

  const fields = useMemo(
    () =>
      getFinalState(pipeline, { ...ctx, exploration }).fields.sort((a, b) => {
        const aisPk = model.primaryKey.includes(a.key);
        const bisPk = model.primaryKey.includes(b.key);
        if (aisPk && !bisPk) {
          return -1;
        }
        if (!aisPk && bisPk) {
          return 1;
        }
        return a.name.localeCompare(b.name);
      }),
    [ctx, exploration, model.primaryKey, pipeline],
  );

  const columnOptions = useMemo(() => fields.map(createFieldOption), [fields]);

  const filteredColumnOptions = useMemo(
    () =>
      columnOptions.filter(({ label }) =>
        label.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase()),
      ),
    [columnOptions, searchTerm],
  );

  const buildNotificationConfig = (): NotificationConfig => {
    switch (notificationType) {
      case 'slack_channel':
        return {
          type: 'slack_channel',
          integrationId: slackIntegration?.integrationId ?? '',
          channelId: selectedChannelId,
        };
      case 'slack':
        return {
          type: 'slack',
          webhook_urls: webhookUrls,
        };
      case 'webhook':
        return {
          type: 'webhook',
          urls: webhookUrls,
        };
    }
  };

  const result: AlertConfiguration = {
    alertConfigurationId: alertConfiguration.alertConfigurationId,
    name,
    message,
    keyFields,
    excludedFields: alertConfiguration.excludedFields,
    pipeline: alertConfiguration.pipeline,
    notificationConfig: buildNotificationConfig(),
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (isValid(result)) {
      onSubmit(result);
    }
  };

  const handleFieldsChange = (values: string[]) => {
    const hiddenSelectedFields = keyFields.filter(
      (field) => !filteredColumnOptions.some((option) => option.value === field),
    );
    setKeyFields([...new Set([...hiddenSelectedFields, ...values])]);
  };

  useEffect(() => {
    if (!displayColumnsFilter && searchTerm.length > 0) {
      setSearchTerm('');
    }
  }, [displayColumnsFilter, searchTerm]);

  return (
    <form
      className={classNames(formStyles.formHorizontal, formStyles.sparse, styles.form)}
      onSubmit={handleSubmit}>
      <div className={formStyles.formRow}>
        <label className={formStyles.formLabel} style={{ width: 60 }}>
          Name
        </label>
        <Input
          required
          autoFocus
          type="text"
          placeholder="Enter value..."
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div className={classNames(formStyles.formRow, formStyles.alignTop)}>
        <label className={formStyles.formLabel} style={{ width: 60 }}>
          Message
        </label>
        <TextArea
          placeholder="Give the alert a descriptive message..."
          value={message}
          rows={3}
          onChange={(e) => setMessage(e.target.value)}
        />
      </div>

      <hr />

      <h3 className={styles.label}>
        Select Columns
        <IconButton
          icon="Search"
          title="Filter columns"
          onClick={() => setDisplayColumnsFilter(!displayColumnsFilter)}
        />
      </h3>

      <p className={formStyles.helpText}>
        We will alert you about every new combination of the selected columns.
      </p>

      {displayColumnsFilter && (
        <Input
          type="text"
          autoFocus
          value={searchTerm}
          placeholder="Filter columns"
          onChange={(event) => setSearchTerm(event.target.value)}
        />
      )}
      <TagSelect
        options={filteredColumnOptions}
        className={formStyles.spaced}
        value={keyFields}
        onChange={handleFieldsChange}
        isMultiSelect
      />

      <hr />

      <h3>Alert Delivery</h3>

      {isNewAlert && hasSlackIntegration && (
        <div className={formStyles.formRow}>
          <RadioButton
            name="notificationType"
            value="slack_channel"
            label="Slack"
            isChecked={notificationType === 'slack_channel'}
            onChange={() => setNotificationType('slack_channel')}
          />{' '}
          <RadioButton
            name="notificationType"
            value="webhook"
            label="Webhook"
            isChecked={notificationType === 'webhook'}
            onChange={() => setNotificationType('webhook')}
          />
        </div>
      )}

      {notificationType === 'slack_channel' && (
        <>
          {isChannelsLoading ? (
            <div className={formStyles.formRow}>
              <Loader />
              <span className={formStyles.helpText}>Loading channels</span>
            </div>
          ) : (
            <>
              <div className={formStyles.formRow}>
                <label className={formStyles.formLabel} style={{ width: 60 }}>
                  Channel
                </label>
                <SearchInput
                  value={selectedChannelId}
                  placeholder="Select a channel..."
                  onChange={setSelectedChannelId}
                  options={[
                    ...slackChannels
                      .filter((c) => c.isBotInChannel)
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((channel) => ({
                        value: channel.id,
                        label: `${channel.isPrivate ? '🔒 ' : '#'}${channel.name}`,
                      })),
                    {
                      label: 'Supersimple not yet in channel, will auto-join',
                      options: slackChannels
                        .filter((c) => !c.isBotInChannel)
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((channel) => ({
                          value: channel.id,
                          label: `#${channel.name}`,
                        })),
                    },
                  ]}
                />
              </div>
            </>
          )}
        </>
      )}

      {(notificationType === 'webhook' || notificationType === 'slack') && (
        <>
          {webhookUrls.map((url, i) => (
            <div key={i} className={formStyles.formRow}>
              <WebhookRow
                url={url}
                onChange={(url) =>
                  setWebhookUrls([...webhookUrls.slice(0, i), url, ...webhookUrls.slice(i + 1)])
                }
              />
              {i > 0 && (
                <IconButton
                  icon="Trash2"
                  size="small"
                  title="Delete"
                  type="gray"
                  onClick={() =>
                    setWebhookUrls([...webhookUrls.slice(0, i), ...webhookUrls.slice(i + 1)])
                  }
                />
              )}
            </div>
          ))}

          <div className={formStyles.formRow}>
            <InlineButton
              size="small"
              onClick={() => {
                setWebhookUrls([...webhookUrls, '']);
              }}>
              + Add Webhook
            </InlineButton>
          </div>
        </>
      )}
      <div className={formStyles.formControls}>
        <Button
          type="submit"
          size="large"
          disabled={!isValid(result) || deleteLoading}
          loading={upsertLoading}>
          {alertConfiguration !== undefined ? 'Save Alert' : 'Create Alert'}
        </Button>
        <Button
          variant="secondary"
          size="large"
          onClick={onCancel}
          disabled={upsertLoading || deleteLoading}>
          Cancel
        </Button>
        {onDelete && alertConfiguration.alertConfigurationId !== undefined && (
          <Button
            icon={<Icon name="Trash2" size={16} />}
            variant="outlined"
            size="large"
            color="warning"
            className={formStyles.floatRight}
            onClick={onDelete}
            disabled={upsertLoading}
            loading={deleteLoading}>
            Delete
          </Button>
        )}
      </div>
    </form>
  );
};

const WebhookRow = ({ url, onChange }: { url: string; onChange: (value: string) => void }) => (
  <div className={formStyles.formRow}>
    <label className={formStyles.formLabel} style={{ width: '100px' }}>
      Webhook URL
    </label>
    <Input
      required
      type="url"
      placeholder="https://..."
      value={url}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

const createEmptyAlertConfiguration = (
  values?: Partial<AlertConfiguration>,
  hasSlackIntegration = false,
): AlertConfiguration => ({
  name: '',
  keyFields: [],
  excludedFields: [],
  pipeline: createDereferencedPipeline({ baseModelId: '', operations: [] }),
  notificationConfig: hasSlackIntegration
    ? {
        type: 'slack_channel',
        integrationId: '',
        channelId: '',
      }
    : {
        type: 'webhook',
        urls: [''],
      },
  ...values,
});

interface AlertFormModalProps {
  alertConfiguration?: Partial<AlertConfiguration>;
  ctx: Omit<PipelineStateContext, 'exploration'>;
  integrations: IntegrationListItem[];
  onClose: () => void;
  onAlertSaved?: () => void;
  onAlertDeleted?: () => void;
}

export const AlertFormModal = (props: AlertFormModalProps) => {
  const { ctx, alertConfiguration, integrations, onClose, onAlertSaved, onAlertDeleted } = props;
  const addToast = useToastContext();
  const account = useSelectedAccount();

  const [upsertMutation, { loading: upsertLoading }] = useUpsertAlertConfigurationMutation();
  const [deleteMutation, { loading: deleteLoading }] = useDeleteAlertConfigurationMutation();

  const isEditing = alertConfiguration?.alertConfigurationId !== undefined;

  const handleDelete = async () => {
    if (alertConfiguration === undefined || alertConfiguration.alertConfigurationId === undefined) {
      throw new Error('Attempting to delete non-existend Alert Configuration.');
    }
    const { alertConfigurationId, name } = alertConfiguration;
    if (confirm(`Are you sure you want to delete Alert "${name}"?`)) {
      try {
        await deleteMutation({
          variables: {
            accountId: account.accountId,
            alertConfigurationId,
          },
        });
      } catch (e) {
        addToast({
          title: 'Error',
          content: () => `Deleting Alert failed: ${getMessage(e)}`,
          kind: 'error',
        });
        return;
      }

      onAlertDeleted && onAlertDeleted();
    }
  };

  const handleSubmit = async (input: AlertConfiguration) => {
    try {
      await upsertMutation({
        variables: { accountId: account.accountId, input },
      });
    } catch (e) {
      addToast({
        title: 'Error',
        content: () => `Saving Alert failed: ${getMessage(e)}`,
        kind: 'error',
      });
      return;
    }

    addToast({
      title: 'Success',
      content: () => `Alert ${isEditing ? 'updated' : 'created'} successfully.`,
      kind: 'success',
    });
    onAlertSaved && onAlertSaved();
  };

  const hasSlackIntegration = integrations.some((i) => i.service === 'slack');

  return (
    <Modal onClose={onClose} closeOnClickAway closeOnEsc>
      {/* SearchInput dropdown needs its own overlay context to stay within the Modal's FocusTrap */}
      <OverlayContextProvider>
        <Panel className={styles.panel}>
          <h1>{isEditing ? 'Edit Alert' : 'Create Alert'}</h1>
          <EditAlertForm
            alertConfiguration={createEmptyAlertConfiguration(
              alertConfiguration,
              hasSlackIntegration,
            )}
            ctx={ctx}
            integrations={integrations}
            onSubmit={handleSubmit}
            onCancel={onClose}
            onDelete={isEditing ? handleDelete : undefined}
            upsertLoading={upsertLoading}
            deleteLoading={deleteLoading}
          />
        </Panel>
      </OverlayContextProvider>
    </Modal>
  );
};
